import { mapActions, mapGetters } from "vuex";
import { showErrorSnackBar, showSuccessSnackBar } from "@/utils/SnackBartHelper";
import Utils from '@/scripts/Utils';
import ProjectsService from "@/services/server/ProjectsService.js";
import UsersService from "@/services/server/UsersService";
import UtilsProjects from "@/pages/Projects/Utils/Utils.js";
import * as echarts from 'echarts';

export default {
  name: "ProjectsDashboard",

  data: () => ({
    isLoading: true,
    projects: [],
    franchisees: [],
    selectedFranchisee: null,
    selectedPeriod: 'last_30_days',
    selectedStep: null,
    franchiseeSearch: '',
    
    // Opções de filtro
    periodOptions: [
      { text: 'Últimos 7 dias', value: 'last_7_days' },
      { text: 'Últimos 30 dias', value: 'last_30_days' },
      { text: 'Últimos 90 dias', value: 'last_90_days' },
      { text: 'Último ano', value: 'last_year' },
      { text: 'Todo período', value: 'all_time' }
    ],
    
    stepOptions: [
      { text: 'Prospecção', value: UtilsProjects.STEPS.PROSPECTING },
      { text: 'Waterscan', value: UtilsProjects.STEPS.DIAGNOSTIC },
      { text: 'Aprovação', value: UtilsProjects.STEPS.APPROVAL },
      { text: 'Fechamento', value: UtilsProjects.STEPS.CLOSING },
      { text: 'Contratos', value: UtilsProjects.STEPS.CONTRACTS },
      { text: 'Entrega', value: UtilsProjects.STEPS.MATERIALS },
      { text: 'Waterfix', value: UtilsProjects.STEPS.INSTALLATION },
      { text: 'Finalizado', value: UtilsProjects.STEPS.FINISH }
    ],

    // Dados calculados
    filteredProjects: [],
    chartInstances: {}
  }),

  computed: {
    ...mapGetters([
      "getCurrentUserData",
      "getCurrentUserRank",
    ]),

    franchiseeOptions() {
      const options = [{ text: 'Todos os Franqueados', value: null }];
      
      // Se há uma busca ativa, filtrar os franqueados
      if (this.franchiseeSearch && this.franchiseeSearch.trim()) {
        const searchTerm = this.franchiseeSearch.toLowerCase().trim();
        const filteredFranchisees = this.franchisees.filter(f => 
          f.name.toLowerCase().includes(searchTerm)
        );
        
        return options.concat(filteredFranchisees.map(f => ({
          text: f.name,
          value: f.id
        })));
      }
      
      // Se não há busca, retornar todos os franqueados
      return options.concat(this.franchisees.map(f => ({
        text: f.name,
        value: f.id
      })));
    },

    totalProjects() {
      return this.filteredProjects.length;
    },

    completedProjects() {
      return this.filteredProjects.filter(p => 
        p.steps.some(step => step.isCurrentStep && step.name === UtilsProjects.STEPS.FINISH)
      ).length;
    },

    inProgressProjects() {
      return this.filteredProjects.filter(p => 
        !p.steps.some(step => step.isCurrentStep && step.name === UtilsProjects.STEPS.FINISH)
      ).length;
    },

    activeFranchisees() {
      const uniqueFranchisees = new Set(
        this.filteredProjects.map(p => p.id_franchisee).filter(Boolean)
      );
      return uniqueFranchisees.size;
    }
  },

  methods: {
    ...mapActions([]),

    async loadData() {
      try {
        this.isLoading = true;
        
        // Carregar projetos
        let status, projectList;
        if (this.getCurrentUserRank === "Super Admin" || this.getCurrentUserRank === "Admin") {
          [status, projectList] = await ProjectsService.getProjectWithUf();
        } else {
          [status, projectList] = await ProjectsService.getProjects();
        }

        if (!status) {
          showErrorSnackBar("Erro ao carregar projetos");
          return;
        }

        this.projects = projectList.map(project => {
          let created_at = new Date(project.created_at._seconds * 1000);
          let currentHourStatusFranchisee = project.currentHourStatusFranchisee;
          let currentHourStatusADM = project.currentHourStatusADM;
          let now = new Date();
          let daysPassed = Math.floor((now - created_at) / (1000 * 60 * 60 * 24));
          
          return {
            ...project,
            created_at,
            created_at_formatted: new Date(created_at).getTime(),
            date_formatted_franchisee: currentHourStatusFranchisee ? new Date(currentHourStatusFranchisee).getTime() : '',
            date_formatted_adm: currentHourStatusADM ? new Date(currentHourStatusADM).getTime() : '',
            running_time: daysPassed,
            currentStepFormatted: this.checkCurrentStep(project.steps)
          };
        });

        
        // Verificar estrutura dos projetos
        if (this.projects.length > 0) {
          const sampleProject = this.projects[0];
          
          // Verificar estrutura dos steps
          if (sampleProject.steps && sampleProject.steps.length > 0) {
          }
          
          // Contar projetos com e sem franqueado
          const projectsWithFranchisee = this.projects.filter(p => p.id_franchisee);
          const projectsWithoutFranchisee = this.projects.filter(p => !p.id_franchisee);
        }

        // Carregar franqueados
        const response = await UsersService.findAllFranchisee();
        if (response.status === 200) {
          this.franchisees = response.data.data;
        } else {
          console.error('Failed to load franchisees:', response);
        }

        this.updateCharts();
        
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        showErrorSnackBar("Erro ao carregar dados do dashboard");
      } finally {
        this.isLoading = false;
      }
    },

    updateCharts() {
      this.filterProjects();
      this.$nextTick(() => {
        this.renderCharts();
      });
    },

    filterProjects() {
      let filtered = [...this.projects];

      // Filtro por franqueado
      if (this.selectedFranchisee) {
        // Se selectedFranchisee é um objeto (do autocomplete), usar o value
        const franchiseeId = typeof this.selectedFranchisee === 'object' 
          ? this.selectedFranchisee.value 
          : this.selectedFranchisee;
        
        if (franchiseeId) {
          filtered = filtered.filter(p => p.id_franchisee === franchiseeId);
        }
      }

      // Filtro por período
      if (this.selectedPeriod !== 'all_time') {
        const now = new Date();
        let startDate;
        
        switch (this.selectedPeriod) {
          case 'last_7_days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'last_30_days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'last_90_days':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'last_year':
            startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            break;
        }
        
        if (startDate) {
          filtered = filtered.filter(p => new Date(p.created_at) >= startDate);
        }
      }

      // Filtro por etapa
      if (this.selectedStep) {
        filtered = filtered.filter(p => 
          p.steps.some(step => step.isCurrentStep && step.name === this.selectedStep)
        );
      }

      this.filteredProjects = filtered;
    },

    renderCharts() {
      this.renderStepDistributionChart();
      this.renderFranchiseeProjectsChart();
      this.renderMonthlyEvolutionChart();
      this.renderStatusDistributionChart();
    },

    renderStepDistributionChart() {
      const chartDom = this.$refs.stepDistributionChart;
      if (!chartDom) return;

      const stepCounts = {};
      this.filteredProjects.forEach(project => {
        const currentStep = project.steps.find(step => step.isCurrentStep);
        if (currentStep) {
          const stepName = this.getStepDisplayName(currentStep.name);
          stepCounts[stepName] = (stepCounts[stepName] || 0) + 1;
        }
      });

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: 'Projetos por Etapa',
            type: 'pie',
            radius: '50%',
            data: Object.entries(stepCounts).map(([name, value]) => ({
              value,
              name
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      };

      this.initChart(chartDom, option, 'stepDistribution');
    },

    renderFranchiseeProjectsChart() {
      const chartDom = this.$refs.franchiseeProjectsChart;
      if (!chartDom) return;

      const franchiseeCounts = {};
      this.filteredProjects.forEach(project => {
        const franchisee = this.franchisees.find(f => f.id === project.id_franchisee);
        const franchiseeName = franchisee ? franchisee.name : 'Não definido';
        franchiseeCounts[franchiseeName] = (franchiseeCounts[franchiseeName] || 0) + 1;
      });

      const sortedEntries = Object.entries(franchiseeCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10); // Top 10

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          boundaryGap: [0, 0.01]
        },
        yAxis: {
          type: 'category',
          data: sortedEntries.map(([name]) => name)
        },
        series: [
          {
            name: 'Projetos',
            type: 'bar',
            data: sortedEntries.map(([, value]) => value),
            itemStyle: {
              color: '#1976D2'
            }
          }
        ]
      };

      this.initChart(chartDom, option, 'franchiseeProjects');
    },

    renderMonthlyEvolutionChart() {
      const chartDom = this.$refs.monthlyEvolutionChart;
      if (!chartDom) return;

      const monthlyData = {};
      this.filteredProjects.forEach(project => {
        const month = Utils.formatDateDayJs(new Date(project.created_at), 'YYYY-MM');
        monthlyData[month] = (monthlyData[month] || 0) + 1;
      });
      

      // Gerar lista completa de meses para os últimos 12 meses
      const now = new Date();
      const monthsList = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = Utils.formatDateDayJs(date, 'YYYY-MM');
        monthsList.push(monthKey);
      }

      // Preencher dados faltantes com 0
      const completeData = monthsList.map(month => ({
        month,
        count: monthlyData[month] || 0
      }));


      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: function (params) {
            const data = params[0];
            return `${data.name}<br/>Projetos: ${data.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: completeData.map(item => item.month),
          axisLabel: {
            formatter: function (value) {
              // Formatar para mostrar apenas Mês/Ano
              const [year, month] = value.split('-');
              const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
                                'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
              return `${monthNames[parseInt(month) - 1]}/${year.slice(2)}`;
            }
          }
        },
        yAxis: {
          type: 'value',
          min: 0
        },
        series: [
          {
            name: 'Projetos Criados',
            type: 'line',
            data: completeData.map(item => item.count),
            smooth: true,
            itemStyle: {
              color: '#4CAF50'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: 'rgba(76, 175, 80, 0.3)'
                }, {
                  offset: 1, color: 'rgba(76, 175, 80, 0.1)'
                }]
              }
            }
          }
        ]
      };

      this.initChart(chartDom, option, 'monthlyEvolution');
    },

    renderStatusDistributionChart() {
      const chartDom = this.$refs.statusDistributionChart;
      if (!chartDom) return;

      const statusCounts = {};
      let unknownStatusCount = 0;

      let nullStatusCount = 0;
      let undefinedStatusCount = 0;

      this.filteredProjects.forEach(project => {
        const statusCode = this.getCurrentUserRank === "Franqueado" ? project.currentStatusFranchisee : project.currentStatusADM;
        
        // Verificar se o status é nulo ou indefinido
        if (statusCode === null) {
          nullStatusCount++;
        } else if (statusCode === undefined) {
          undefinedStatusCount++;
        }
        
        const status = this.getProjectStatus(project);
        
        if (status === 'Desconhecido') {
          unknownStatusCount++;
        } else {
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        }
      });


      // Adicionar status desconhecidos se houver
      if (unknownStatusCount > 0) {
        statusCounts['Status Desconhecido'] = unknownStatusCount;
      }

      // Adicionar projetos sem status se houver
      if (nullStatusCount > 0) {
        statusCounts['Sem Status (Null)'] = nullStatusCount;
      }

      if (undefinedStatusCount > 0) {
        statusCounts['Sem Status (Undefined)'] = undefinedStatusCount;
      }


      const option = {
        tooltip: {
          trigger: 'item'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: 'Status dos Projetos',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '30',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: Object.entries(statusCounts).map(([name, value]) => ({
              value,
              name,
              itemStyle: {
                color: this.getStatusColor(name)
              }
            }))
          }
        ]
      };

      this.initChart(chartDom, option, 'statusDistribution');
    },

    renderFranchiseePerformanceChart() {
      const chartDom = this.$refs.franchiseePerformanceChart;
      if (!chartDom) {
        return;
      }

      const performanceData = this.calculateFranchiseePerformance();
      
      if (!performanceData || performanceData.length === 0) {
        return;
      }
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['Projetos Total', 'Projetos Finalizados', 'Taxa de Conversão (%)']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: performanceData.map(item => item.name)
        },
        yAxis: [
          {
            type: 'value',
            name: 'Quantidade',
            position: 'left'
          },
          {
            type: 'value',
            name: 'Taxa (%)',
            position: 'right'
          }
        ],
        series: [
          {
            name: 'Projetos Total',
            type: 'bar',
            data: performanceData.map(item => item.total),
            itemStyle: { color: '#1976D2' }
          },
          {
            name: 'Projetos Finalizados',
            type: 'bar',
            data: performanceData.map(item => item.completed),
            itemStyle: { color: '#4CAF50' }
          },
          {
            name: 'Taxa de Conversão (%)',
            type: 'line',
            yAxisIndex: 1,
            data: performanceData.map(item => item.conversionRate),
            itemStyle: { color: '#FF9800' }
          }
        ]
      };

      this.initChart(chartDom, option, 'franchiseePerformance');
    },

    renderAverageTimeChart() {
      const chartDom = this.$refs.averageTimeChart;
      if (!chartDom) return;

      const averageTimes = this.calculateAverageTimeByStep();
      
      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: function (params) {
            return params[0].name + '<br/>' +
                   'Tempo Médio: ' + params[0].value + ' dias';
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: averageTimes.map(item => item.step)
        },
        yAxis: {
          type: 'value',
          name: 'Dias'
        },
        series: [
          {
            name: 'Tempo Médio',
            type: 'bar',
            data: averageTimes.map(item => item.averageTime),
            itemStyle: {
              color: function(params) {
                const colors = ['#1976D2', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#795548', '#607D8B'];
                return colors[params.dataIndex % colors.length];
              }
            }
          }
        ]
      };

      this.initChart(chartDom, option, 'averageTime');
    },

    renderConversionRateChart() {
      const chartDom = this.$refs.conversionRateChart;
      if (!chartDom) return;

      const conversionRates = this.calculateConversionRates();
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c}%'
        },
        series: [
          {
            name: 'Taxa de Conversão',
            type: 'pie',
            radius: '70%',
            data: conversionRates,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      };

      this.initChart(chartDom, option, 'conversionRate');
    },

    initChart(dom, option, chartName) {
      if (this.chartInstances[chartName]) {
        this.chartInstances[chartName].dispose();
      }
      
      const chart = echarts.init(dom);
      chart.setOption(option);
      this.chartInstances[chartName] = chart;
      
      // Responsive
      window.addEventListener('resize', () => {
        chart.resize();
      });
    },

    calculateFranchiseePerformance() {
      const performance = {};
      
      
      this.filteredProjects.forEach(project => {
        const franchisee = this.franchisees.find(f => f.id === project.id_franchisee);
        const franchiseeName = franchisee ? franchisee.name : 'Não definido';
        
        
        if (!performance[franchiseeName]) {
          performance[franchiseeName] = {
            name: franchiseeName,
            total: 0,
            completed: 0
          };
        }
        
        performance[franchiseeName].total++;
        
        const isCompleted = project.steps.some(step => 
          step.isCurrentStep && step.name === UtilsProjects.STEPS.FINISH
        );
        
        if (isCompleted) {
          performance[franchiseeName].completed++;
        }
      });

      const result = Object.values(performance)
        .map(item => ({
          ...item,
          conversionRate: item.total > 0 ? Math.round((item.completed / item.total) * 100) : 0
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10); // Top 10

      return result;
    },

    calculateAverageTimeByStep() {
      const stepTimes = {};
      
      this.filteredProjects.forEach(project => {
        project.steps.forEach(step => {
          if (step.enteredAt && step.exitedAt) {
            const enteredDate = new Date(step.enteredAt);
            const exitedDate = new Date(step.exitedAt);
            const daysDiff = Math.ceil((exitedDate - enteredDate) / (1000 * 60 * 60 * 24));
            
            const stepName = this.getStepDisplayName(step.name);
            
            if (!stepTimes[stepName]) {
              stepTimes[stepName] = [];
            }
            
            stepTimes[stepName].push(daysDiff);
          }
        });
      });

      return Object.entries(stepTimes)
        .map(([step, times]) => ({
          step,
          averageTime: Math.round(times.reduce((a, b) => a + b, 0) / times.length)
        }))
        .sort((a, b) => b.averageTime - a.averageTime);
    },

    calculateConversionRates() {
      
      const rates = [
        { name: 'Prospecção → Waterscan', value: this.calculateStepConversion(UtilsProjects.STEPS.PROSPECTING, UtilsProjects.STEPS.DIAGNOSTIC) },
        { name: 'Waterscan → Aprovação', value: this.calculateStepConversion(UtilsProjects.STEPS.DIAGNOSTIC, UtilsProjects.STEPS.APPROVAL) },
        { name: 'Aprovação → Fechamento', value: this.calculateStepConversion(UtilsProjects.STEPS.APPROVAL, UtilsProjects.STEPS.CLOSING) },
        { name: 'Fechamento → Contratos', value: this.calculateStepConversion(UtilsProjects.STEPS.CLOSING, UtilsProjects.STEPS.CONTRACTS) },
        { name: 'Contratos → Entrega', value: this.calculateStepConversion(UtilsProjects.STEPS.CONTRACTS, UtilsProjects.STEPS.MATERIALS) },
        { name: 'Entrega → Waterfix', value: this.calculateStepConversion(UtilsProjects.STEPS.MATERIALS, UtilsProjects.STEPS.INSTALLATION) },
        { name: 'Waterfix → Finalizado', value: this.calculateStepConversion(UtilsProjects.STEPS.INSTALLATION, UtilsProjects.STEPS.FINISH) }
      ];

      const filteredRates = rates.filter(rate => rate.value > 0);
      
      return filteredRates;
    },

    calculateStepConversion(fromStep, toStep) {
      // Projetos que passaram pela etapa inicial
      const fromProjects = this.filteredProjects.filter(p => 
        p.steps.some(step => step.name === fromStep)
      );
      
      // Projetos que passaram pela etapa inicial E pela etapa seguinte
      const convertedProjects = fromProjects.filter(p => {
        const fromStepIndex = p.steps.findIndex(step => step.name === fromStep);
        const toStepIndex = p.steps.findIndex(step => step.name === toStep);
        
        // Verificar se a etapa seguinte aconteceu DEPOIS da etapa inicial
        return fromStepIndex !== -1 && toStepIndex !== -1 && toStepIndex > fromStepIndex;
      });
      
      const conversionRate = fromProjects.length > 0 ? Math.round((convertedProjects.length / fromProjects.length) * 100) : 0;
      
      return conversionRate;
    },

    getStepDisplayName(stepName) {
      const stepNames = {
        [UtilsProjects.STEPS.PROSPECTING]: 'Prospecção',
        [UtilsProjects.STEPS.DIAGNOSTIC]: 'Waterscan',
        [UtilsProjects.STEPS.APPROVAL]: 'Aprovação',
        [UtilsProjects.STEPS.CLOSING]: 'Fechamento',
        [UtilsProjects.STEPS.CONTRACTS]: 'Contratos',
        [UtilsProjects.STEPS.MATERIALS]: 'Entrega',
        [UtilsProjects.STEPS.INSTALLATION]: 'Waterfix',
        [UtilsProjects.STEPS.FINISH]: 'Finalizado'
      };
      
      return stepNames[stepName] || stepName;
    },

    getProjectStatus(project) {
      const status = this.getCurrentUserRank === "Franqueado" ? 
        project.currentStatusFranchisee : 
        project.currentStatusADM;
      
      // Usar a referência completa de status
      const statusReference = this.getStatusReference();
      const statusInfo = statusReference[status];
      
      if (statusInfo) {
        return statusInfo.name;
      }
      
      return 'Desconhecido';
    },

    getStatusReference() {
      // Importar dinamicamente a referência de status
      const { StatusReference } = require("@/pages/Projects/Utils/StatusConstants.js");
      return StatusReference;
    },

    getStatusColor(status) {
      // Mapear cores baseado no nome do status
      const colorMap = {
        // Cores baseadas na referência de status
        'Reprovada': '#ee7b6b',
        'Reprovado': '#ee7b6b',
        'Em revisão': '#d1c300',
        'Revisão Solicitada': '#f0a26e',
        'Prospecção Aprovado': '#69c871',
        'Prospecção Aprovada': '#69c871',
        'Waterscan Aprovado': '#69c871',
        'Analise Solicitada': '#f0a26e',
        'Analise em andamento': '#d1c300',
        'Analise em andamento.': '#d1c300',
        'Rascunho Salvo': '#7ca3de',
        'Waterscan Liberado': '#69c871',
        'Propostas Liberadas': '#69c871',
        'Propostas Pendentes': '#f0a26e',
        'Propostas em Produção': '#d1c300',
        'Fechamento Liberado': '#69c871',
        'Contrato Assinado': '#69c871',
        'Contrato Aprovado': '#69c871',
        'Alteração de Contrato': '#f0a26e',
        'Contrato Adicionado': '#d1c300',
        'Contrato Solicitado': '#f0a26e',
        'Aguardando Contrato': '#d1c300',
        'Contrato Disponivel': '#f0a26e',
        'Enviado para Assinatura': '#d1c300',
        'Itens Recebidos': '#69c871',
        'Aguardando Recebimento': '#d1c300',
        'Expedição Solicitada': '#f0a26e',
        'Aguardando Expedição': '#d1c300',
        'Itens Enviados': '#f0a26e',
        'Waterfix Aprovado': '#69c871',
        'Declinado': '#ee7b6b',
        'Status Desconhecido': '#9E9E9E',
        'Sem Status (Null)': '#FF5722',
        'Sem Status (Undefined)': '#FF5722'
      };
      
      return colorMap[status] || '#9E9E9E';
    },

    checkCurrentStep(steps) {
      const step = steps.find(({isCurrentStep}) => isCurrentStep === true);
      return step ? UtilsProjects.TranslateSteps[step.name] : " - / -";
    },

    async refreshData() {
      await this.loadData();
    }
  },

  async created() {
    await this.loadData();
  },

  beforeDestroy() {
    // Limpar instâncias dos gráficos
    Object.values(this.chartInstances).forEach(chart => {
      if (chart) {
        chart.dispose();
      }
    });
  }
};
